
import React, { useState, useEffect, useMemo } from 'react';
import { X, Info, PlusCircle, Trash2, Calendar, HelpCircle } from 'lucide-react';
import { Patient, ClinicalCycle, CycleScore, Therapist, Indicator } from '../types';
import { INDICATORS } from '../constants';

interface CycleFormProps {
  patient: Patient;
  therapist: Therapist;
  onClose: () => void;
  onSubmit: (cycle: ClinicalCycle) => Promise<void> | void;
  initialData?: ClinicalCycle;
}

const CycleForm: React.FC<CycleFormProps> = ({ patient, therapist, onClose, onSubmit, initialData }) => {
  const [scores, setScores] = useState<CycleScore[]>([]);
  const [functionalImpairment, setFunctionalImpairment] = useState(initialData?.functionalImpairment || 5);
  const [subjectiveDistress, setSubjectiveDistress] = useState(initialData?.subjectiveDistress || 5);
  const [notes, setNotes] = useState(initialData?.clinicalNotes || '');
  const [cycleDate, setCycleDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddIndicator, setShowAddIndicator] = useState(false);
  const [hoveredIndicator, setHoveredIndicator] = useState<string | null>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setScores(initialData.scores);
    } else {
      setScores((patient.selectedIndicatorIds ?? []).map(id => ({ indicatorId: id, score: 5 })));
    }
  }, [initialData, patient.selectedIndicatorIds]);

  // Agrupamento de indicadores disponíveis para adicionar
  const groupedAvailableIndicators = useMemo(() => {
    return INDICATORS.reduce((acc, ind) => {
      if (!acc[ind.dominio]) acc[ind.dominio] = [];
      acc[ind.dominio].push(ind);
      return acc;
    }, {} as Record<string, Indicator[]>);
  }, []);

  const handleScoreChange = (id: string, value: number) => {
    setScores(prev => prev.map(s => s.indicatorId === id ? { ...s, score: value } : s));
  };

  const removeIndicator = (id: string) => {
    setScores(prev => prev.filter(s => s.indicatorId !== id));
  };

  const addIndicator = (id: string) => {
    if (!scores.some(s => s.indicatorId === id)) {
      setScores(prev => [...prev, { indicatorId: id, score: 5 }]);
    }
    setShowAddIndicator(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const newCycle: ClinicalCycle = {
        id: initialData?.id || Date.now().toString(),
        date: cycleDate,
        approach: patient.activeApproach,
        scores,
        clinicalNotes: notes,
        functionalImpairment,
        subjectiveDistress,
        copingResources: 5,
        aiInsight: initialData?.aiInsight,
        patientReport: initialData?.patientReport,
        evolutionGuidelines: initialData?.evolutionGuidelines
      };

      await onSubmit(newCycle);
    } finally {
      setIsSubmitting(false);
    }
  };

  const COMPOSITE_EXPLANATIONS = {
    impairment: "Reflete o quanto os sintomas interferem na vida prática: trabalho, estudos, relações sociais e atividades básicas da rotina.",
    distress: "Reflete a intensidade da dor emocional, ansiedade ou desconforto relatado pelo paciente, independentemente de ele conseguir ser funcional ou não."
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 border dark:border-slate-800 transition-colors duration-300">
        <div className="p-5 sm:p-8 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50/80 dark:bg-slate-800/50">
          <div>
            <h3 className="text-xl font-black text-slate-800 dark:text-slate-100">{initialData ? 'Editar Registro' : 'Novo Ciclo'}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1">Paciente: {patient.name}</p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-2xl transition-colors"><X size={20} className="text-slate-500 dark:text-slate-400" /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-8">
          <div className="space-y-1.5 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 rounded-2xl border border-indigo-100 dark:border-indigo-900/40 shadow-sm">
            <label className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-widest ml-1">Data de Referência do Ciclo</label>
            <div className="relative">
              <input 
                type="date" 
                value={cycleDate} 
                onChange={(e) => setCycleDate(e.target.value)} 
                className="w-full p-3 bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-900/50 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-all text-sm font-bold pl-10 dark:text-slate-100"
              />
              <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-indigo-400" size={16} />
            </div>
          </div>

          <section>
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-2">Indicadores de Evolução</h4>
              <button type="button" onClick={() => setShowAddIndicator(!showAddIndicator)} className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors">
                <PlusCircle size={14} /> Adicionar Indicador
              </button>
            </div>

            {showAddIndicator && (
              <div className="mb-6 p-6 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-3xl space-y-4 animate-in fade-in slide-in-from-top-2 overflow-y-auto max-h-[300px] custom-scrollbar">
                {/* Fix: Explicitly cast Object.entries to resolve 'unknown' type error for 'items' */}
                {(Object.entries(groupedAvailableIndicators) as [string, Indicator[]][]).map(([domain, items]) => (
                  <div key={domain} className="space-y-2">
                    <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b dark:border-slate-700 pb-1">{domain}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {items.map(ind => (
                        <button 
                          key={ind.id} type="button" 
                          onClick={() => addIndicator(ind.id)}
                          className={`text-left text-[10px] font-bold p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-indigo-400 transition-colors shadow-sm dark:text-slate-300 ${scores.some(s => s.indicatorId === ind.id) ? 'opacity-40 cursor-not-allowed' : ''}`}
                          disabled={scores.some(s => s.indicatorId === ind.id)}
                        >
                          {ind.nome}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-8">
              {scores.map(s => {
                const ind = INDICATORS.find(i => i.id === s.indicatorId);
                return (
                  <div 
                    key={s.indicatorId} 
                    className="group space-y-3 relative"
                    onMouseEnter={() => setHoveredIndicator(s.indicatorId)}
                    onMouseLeave={() => setHoveredIndicator(null)}
                  >
                    <div className="flex justify-between items-center px-1">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">{ind?.dominio}</p>
                          <label className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                            {ind?.nome}
                            <Info size={12} className="text-slate-300 dark:text-slate-600" />
                          </label>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => removeIndicator(s.indicatorId)} 
                          className="flex items-center justify-center p-1.5 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-all shadow-sm opacity-0 group-hover:opacity-100"
                          title="Remover indicador deste ciclo"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <span className="text-lg font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 rounded-xl border border-indigo-100 dark:border-indigo-800 min-w-[3rem] text-center">{s.score}</span>
                    </div>
                    <input 
                      type="range" min="0" max="10" step="1" 
                      value={s.score}
                      onChange={(e) => handleScoreChange(s.indicatorId, parseInt(e.target.value))}
                      className="w-full h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full appearance-none cursor-pointer accent-indigo-600"
                    />

                    {hoveredIndicator === s.indicatorId && ind && (
                      <div className="absolute z-10 left-0 right-0 -top-2 -translate-y-full bg-slate-900 text-white p-3 rounded-xl text-[10px] shadow-xl animate-in fade-in zoom-in-95 pointer-events-none border border-slate-700">
                        <p className="leading-relaxed font-medium opacity-90">{ind.descricao_clinica}</p>
                        <div className="absolute -bottom-1.5 left-8 w-3 h-3 bg-slate-900 rotate-45 border-r border-b border-slate-700"></div>
                      </div>
                    )}
                  </div>
                );
              })}
              {scores.length === 0 && (
                <div className="text-center p-5 sm:p-8 border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-3xl text-slate-400 text-xs italic">
                  Nenhum indicador selecionado para este ciclo. Adicione indicadores acima.
                </div>
              )}
            </div>
          </section>

          <section className="bg-slate-50 dark:bg-slate-800/30 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 space-y-8">
            <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">Métricas Compostas (IA)</h4>
            <div className="space-y-8">
              {[
                { id: 'impairment', label: 'Prejuízo Funcional', val: functionalImpairment, set: setFunctionalImpairment, tooltip: COMPOSITE_EXPLANATIONS.impairment },
                { id: 'distress', label: 'Sofrimento Subjetivo', val: subjectiveDistress, set: setSubjectiveDistress, tooltip: COMPOSITE_EXPLANATIONS.distress }
              ].map((item, i) => (
                <div key={i} className="space-y-3 relative">
                  <div className="flex justify-between font-black text-xs text-slate-700 dark:text-slate-300 uppercase tracking-widest px-1">
                    <div className="flex items-center gap-1.5 group/label cursor-help" onMouseEnter={() => setActiveTooltip(item.id)} onMouseLeave={() => setActiveTooltip(null)}>
                      <span>{item.label}</span>
                      <HelpCircle size={14} className="text-slate-300 dark:text-slate-600 group-hover/label:text-indigo-500 transition-colors" />
                    </div>
                    <span className="text-indigo-600 dark:text-indigo-400">{item.val}</span>
                  </div>
                  
                  {activeTooltip === item.id && (
                    <div className="absolute z-10 left-0 right-0 -top-2 -translate-y-full bg-indigo-900 text-white p-4 rounded-2xl text-[11px] shadow-2xl animate-in fade-in slide-in-from-bottom-2 border border-indigo-700 leading-relaxed font-medium">
                      {item.tooltip}
                      <div className="absolute -bottom-1.5 left-6 w-3 h-3 bg-indigo-900 rotate-45 border-r border-b border-indigo-700"></div>
                    </div>
                  )}

                  <input type="range" min="0" max="10" value={item.val} onChange={(e) => item.set(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer accent-slate-700 dark:accent-slate-400" />
                  <div className="flex justify-between text-[9px] font-black uppercase tracking-widest">
                    <span className="text-green-600 dark:text-green-400">Equilíbrio</span>
                    <span className="text-red-600 dark:text-red-400">Crítico</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Observações Clínicas (Sigiloso)</label>
            <textarea 
              className="w-full p-4 border border-slate-200 dark:border-slate-700 rounded-[2rem] bg-white dark:bg-slate-800 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-all min-h-[140px] text-sm leading-relaxed dark:text-slate-200"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Descreva detalhes qualitativos observados durante este ciclo..."
            />
          </section>
        </form>

        <div className="p-5 sm:p-8 border-t dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/50 flex gap-4">
          <button onClick={onClose} className="flex-1 p-4 border border-rose-200 dark:border-rose-900/30 bg-rose-50 dark:bg-rose-950/20 rounded-2xl font-black text-rose-500 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all shadow-sm uppercase text-xs tracking-widest leading-none">Descartar</button>
          <button 
            onClick={handleSubmit} 
            disabled={isSubmitting} 
            className="flex-1 p-4 bg-emerald-600 text-white rounded-2xl font-black hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 dark:shadow-none disabled:opacity-50 uppercase text-xs tracking-widest leading-none"
          >
            {isSubmitting ? 'Iniciando Processamento...' : 'Registrar Ciclo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CycleForm;
